import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTCoins, TCOINS_TYPES } from '../../context/TCoinsContext';
import { useCoins } from '../../context/CoinContext';
import { useMarketplace } from '../../context/MarketplaceContext';
import { useTheme } from '../../context/ThemeContext';
import { PACKAGE_TYPES, PACKAGES } from '../../data/packageModels';
import { doc, getDoc, collection, addDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { createStoreModel } from '../../data/marketplaceModels';

const FreeTrialPromotionModal = ({ isOpen, onClose, campaign }) => {
  const { currentUser } = useAuth();
  const { addTCoins, loadTCoins, deductTCoins } = useTCoins();
  const { addCoins } = useCoins();
  const { subscribeToPackage, getStoreSubscription } = useMarketplace();
  const { currentTheme } = useTheme();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [userStoreId, setUserStoreId] = useState(null);
  const [bonusClaimed, setBonusClaimed] = useState(false);
  const [packageSubscribed, setPackageSubscribed] = useState(false);
  const [trialEndDate, setTrialEndDate] = useState(null);
  const hasCheckedClaimRef = useRef(false); // Use ref instead of state to prevent re-renders
  const lastCampaignIdRef = useRef(null); // Track last campaign ID

  // Get values from campaign or use defaults (MUST be declared before useEffect)
  const BONUS_AMOUNT = campaign?.features?.bonusCoins?.amount || 3000;
  const TRIAL_DAYS = campaign?.features?.packageTrial?.trialDays || 14;
  const PACKAGE_TYPE = campaign?.features?.packageTrial?.packageType || PACKAGE_TYPES.PREMIUM;
  
  // Check which features are enabled (MUST be declared before useEffect)
  const hasBonusCoins = campaign?.features?.bonusCoins?.enabled || false;
  const hasPackageTrial = campaign?.features?.packageTrial?.enabled || false;
  
  // Campaign ID for tracking
  const campaignId = campaign?.id || 'free-trial-promotion';

  // Debug: Log modal render (MUST be after variable declarations)
  useEffect(() => {
    if (isOpen && campaign) {
      console.log('[FreeTrialModal] Modal opened:', { 
        campaignId: campaign?.id, 
        campaignFeatures: campaign?.features,
        hasBonusCoins,
        hasPackageTrial,
        bonusCoinsEnabled: campaign?.features?.bonusCoins?.enabled,
        packageTrialEnabled: campaign?.features?.packageTrial?.enabled
      });
    }
  }, [isOpen, campaign?.id, campaign?.features, hasBonusCoins, hasPackageTrial]);

  // Reset states when modal opens with a new campaign
  useEffect(() => {
    if (isOpen && campaign?.id && campaign?.id !== lastCampaignIdRef.current) {
      lastCampaignIdRef.current = campaign.id;
      hasCheckedClaimRef.current = false;
      setCurrentStep(1);
      setBonusClaimed(false);
      setPackageSubscribed(false);
      setTrialEndDate(null);
      setUserStoreId(null);
    } else if (!isOpen) {
      // Reset when modal closes
      lastCampaignIdRef.current = null;
      hasCheckedClaimRef.current = false;
    }
  }, [isOpen, campaign?.id]);

  // Check user data only once when modal opens
  useEffect(() => {
    if (isOpen && currentUser && campaign && !hasCheckedClaimRef.current) {
      hasCheckedClaimRef.current = true;
      // Use setTimeout to prevent infinite loop
      setTimeout(() => {
        checkUserStore();
        checkIfAlreadyClaimed();
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentUser?.uid, campaign?.id]);

  const checkUserStore = async () => {
    if (!currentUser) return;
    
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        if (userData.storeId) {
          setUserStoreId(userData.storeId);
        }
      }
    } catch (error) {
      console.error('Error checking user store:', error);
    }
  };

  const checkIfAlreadyClaimed = async () => {
    if (!currentUser || !campaign) return;
    
    const currentCampaignId = campaign.id || 'free-trial-promotion';
    const currentHasBonusCoins = campaign?.features?.bonusCoins?.enabled || false;
    const currentBonusAmount = campaign?.features?.bonusCoins?.amount || 3000;
    
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        
        // ตรวจสอบจาก transactions
        const transactions = userData.transactions || [];
        const hasClaimedBonus = transactions.some(t => 
          t.reason?.includes('Campaign') && 
          t.reason?.includes(currentCampaignId) &&
          t.type === 'add' && 
          t.coinType === TCOINS_TYPES.VIRTUAL &&
          (currentHasBonusCoins ? t.amount === currentBonusAmount : true)
        );
        
        // ตรวจสอบจาก promotionClaims field
        const promotionClaims = userData.promotionClaims || {};
        const hasClaimedPromotion = promotionClaims[currentCampaignId] === true;
        
        if (hasClaimedBonus || hasClaimedPromotion) {
          setBonusClaimed(true);
          
          // ถ้ามี Package Trial ให้ตรวจสอบว่าสมัครแล้วหรือยัง
          if (hasPackageTrial && userData.storeId) {
            // ตรวจสอบว่ามี package หรือยัง
            const subscription = await getStoreSubscription(userData.storeId);
            if (subscription && subscription.packageType !== PACKAGE_TYPES.FREE) {
              setPackageSubscribed(true);
              if (subscription.endDate) {
                setTrialEndDate(subscription.endDate.toDate ? subscription.endDate.toDate() : new Date(subscription.endDate));
              }
              setCurrentStep(3); // ไปที่ success screen (รับครบแล้ว)
            } else {
              // รับ Bonus แล้ว แต่ยังไม่ได้สมัคร Package
              setCurrentStep(1); // อยู่ที่ step 1 แต่แสดงว่า "รับแล้ว"
            }
          } else if (hasPackageTrial && !userData.storeId) {
            // รับ Bonus แล้ว แต่ยังไม่มีร้าน (ต้องสมัคร Package)
            setCurrentStep(1); // อยู่ที่ step 1 แต่แสดงว่า "รับแล้ว"
          } else {
            // รับ Bonus แล้ว และไม่มี Package Trial
            setCurrentStep(3); // ไปที่ success screen
          }
        }
      }
    } catch (error) {
      console.error('Error checking if already claimed:', error);
    }
  };

  const handleStep1ClaimBonus = async () => {
    if (!currentUser || loading || bonusClaimed) {
      // ถ้ารับแล้ว ให้แสดงข้อความ
      if (bonusClaimed) {
        alert('คุณได้รับสิทธิ์นี้แล้ว');
      }
      return;
    }

    // ตรวจสอบอีกครั้งก่อนรับ (ป้องกัน double-click และ multiple claims)
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        const promotionClaims = userData.promotionClaims || {};
        
        // ตรวจสอบจาก promotionClaims
        if (promotionClaims[campaignId] === true) {
          alert('คุณได้รับสิทธิ์นี้แล้ว');
          setBonusClaimed(true);
          // ถ้ามี Package Trial ให้ไป step 2, ถ้าไม่มีให้ไป step 3
          if (hasPackageTrial) {
            setCurrentStep(2);
          } else {
            setCurrentStep(3);
          }
          return;
        }
        
        // ตรวจสอบจาก transactions (เพิ่มความปลอดภัย)
        const transactions = userData.transactions || [];
        const hasClaimedFromTransaction = transactions.some(t => 
          t.reason?.includes('Campaign') && 
          t.reason?.includes(campaignId) &&
          t.type === 'add' && 
          t.coinType === TCOINS_TYPES.VIRTUAL &&
          (hasBonusCoins ? t.amount === BONUS_AMOUNT : true)
        );
        
        if (hasClaimedFromTransaction) {
          alert('คุณได้รับสิทธิ์นี้แล้ว');
          setBonusClaimed(true);
          // ถ้ามี Package Trial ให้ไป step 2, ถ้าไม่มีให้ไป step 3
          if (hasPackageTrial) {
            setCurrentStep(2);
          } else {
            setCurrentStep(3);
          }
          return;
        }
      }
    } catch (error) {
      console.error('Error checking promotion claim:', error);
      alert('เกิดข้อผิดพลาดในการตรวจสอบสิทธิ์');
      return;
    }

    try {
      setLoading(true);
      
      // Add bonus T Coins (Virtual - cannot exchange) - ถ้ามี feature นี้
      let success = true;
      if (hasBonusCoins && BONUS_AMOUNT > 0) {
        success = await addTCoins(
          currentUser.uid,
          TCOINS_TYPES.VIRTUAL,
          BONUS_AMOUNT,
          `Campaign ${campaignId} - Bonus Coins`
        );
      }

      if (success) {
        // บันทึกว่า user รับสิทธิ์แล้ว
        const userRef = doc(db, 'users', currentUser.uid);
        await updateDoc(userRef, {
          [`promotionClaims.${campaignId}`]: true,
          [`promotionClaims.${campaignId}-claimedAt`]: new Date()
        });
        
        setBonusClaimed(true);
        if (hasBonusCoins) {
          await loadTCoins(currentUser.uid); // Reload T Coins with userId
        }
        setTimeout(() => {
          // ถ้ามี Package Trial ให้ไป step 2, ถ้าไม่มีให้ไป step 3
          if (hasPackageTrial) {
            setCurrentStep(2);
          } else {
            setCurrentStep(3);
          }
          setLoading(false);
        }, 1500);
      } else {
        alert('เกิดข้อผิดพลาดในการรับโบนัส');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error claiming bonus:', error);
      alert('เกิดข้อผิดพลาด: ' + error.message);
      setLoading(false);
    }
  };

  const handleStep2SubscribePackage = async () => {
    if (!currentUser || loading) return;

    try {
      setLoading(true);

      // If user doesn't have a store, create one first
      let storeId = userStoreId;
      
      if (!storeId) {
        // Create store with basic info (Free for promotion)
        try {
          // Create store directly (free for promotion)
          const storeModel = createStoreModel({
            name: currentUser.displayName || currentUser.email?.split('@')[0] || 'ร้านค้าของฉัน',
            description: 'ร้านค้าจาก Free Trial Promotion',
            ownerId: currentUser.uid,
            ownerRole: currentUser.role || 'user',
            category: 'General',
            contact: {
              phone: '',
              email: currentUser.email || '',
              lineId: '',
              address: ''
            },
            logo: '',
            coverImage: '',
            status: 'active',
            createdAt: new Date(),
            openedAt: new Date()
          });

          const storeRef = await addDoc(collection(db, 'stores'), storeModel);
          
          // Update user document
          const userRef = doc(db, 'users', currentUser.uid);
          await updateDoc(userRef, {
            storeId: storeRef.id,
            hasStore: true
          });

          // Add coins to user account (for opening store - normally costs 750 coins)
          // But for promotion, we give it for free by adding coins
          await addCoins(750);

          storeId = storeRef.id;
          setUserStoreId(storeId);
        } catch (error) {
          console.error('Error creating store:', error);
          throw new Error('ไม่สามารถสร้างร้านค้าได้: ' + error.message);
        }
      }

      // Subscribe to Package (Free Trial) - ถ้ามี feature นี้
      if (!hasPackageTrial) {
        // ถ้าไม่มี Package Trial ให้ข้ามไป step 3
        setCurrentStep(3);
        setLoading(false);
        return;
      }

      const trialEndDate = new Date();
      trialEndDate.setDate(trialEndDate.getDate() + TRIAL_DAYS);
      
      const result = await subscribeToPackage(
        storeId,
        PACKAGE_TYPE,
        'bonus-coins', // Payment method (Bonus Coins from Campaign)
        campaignId, // Transaction ID
        trialEndDate, // End date
        TRIAL_DAYS // Trial days
      );

      if (result && result.success) {
        setPackageSubscribed(true);
        
        // Calculate trial end date
        const endDate = new Date();
        endDate.setDate(endDate.getDate() + TRIAL_DAYS);
        setTrialEndDate(endDate);

        setTimeout(() => {
          setCurrentStep(3);
          setLoading(false);
        }, 1500);
      } else {
        alert('เกิดข้อผิดพลาดในการสมัคร Package');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error subscribing to package:', error);
      alert('เกิดข้อผิดพลาด: ' + error.message);
      setLoading(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  // Show login prompt if user is not logged in
  if (!currentUser) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4">
        <div 
          className="rounded-2xl max-w-md w-full shadow-2xl relative"
          style={{ backgroundColor: currentTheme.colors.background }}
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div className="p-8 text-center">
            <div className="mb-6">
              <div className="w-24 h-24 mx-auto bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center shadow-lg">
                <span className="text-5xl">🔒</span>
              </div>
            </div>
            
            <h2 
              className="text-2xl font-bold mb-3"
              style={{ color: currentTheme.colors['text-primary'] }}
            >
              กรุณาเข้าสู่ระบบก่อน
            </h2>
            
            <p 
              className="mb-6"
              style={{ color: currentTheme.colors['text-secondary'] }}
            >
              เพื่อรับโบนัสและเริ่มทดลองใช้ Premium Package ฟรี 14 วัน
            </p>

            <div className="space-y-3">
              <button
                onClick={() => {
                  onClose();
                  window.location.href = '/register';
                }}
                className="w-full py-3 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all"
                style={{
                  background: `linear-gradient(to right, ${currentTheme.colors.primary}, ${currentTheme.colors.accent})`
                }}
              >
                ลงทะเบียน / เข้าสู่ระบบ
              </button>
              
              <button
                onClick={onClose}
                className="w-full py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                ปิด
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4" onClick={(e) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    }}>
      <div 
        className="rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl relative"
        style={{ backgroundColor: currentTheme.colors.background }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Step 1: Claim Bonus (ถ้ามี Bonus Coins) */}
        {currentStep === 1 && hasBonusCoins && (
          <Step1ClaimBonus
            bonusAmount={BONUS_AMOUNT}
            onClaim={handleStep1ClaimBonus}
            loading={loading}
            claimed={bonusClaimed}
            currentTheme={currentTheme}
            onAdvance={() => {
              if (hasPackageTrial) {
                setCurrentStep(2);
              } else {
                setCurrentStep(3);
              }
            }}
          />
        )}

        {/* Step 1 Alternative: ถ้าไม่มี Bonus Coins แต่มี Package Trial */}
        {currentStep === 1 && !hasBonusCoins && hasPackageTrial && (
          <Step2SubscribePackage
            onSubscribe={handleStep2SubscribePackage}
            loading={loading}
            subscribed={packageSubscribed}
            hasStore={!!userStoreId}
            packageType={PACKAGE_TYPE}
            trialDays={TRIAL_DAYS}
          />
        )}

        {/* Step 2: Subscribe Package */}
        {currentStep === 2 && hasPackageTrial && (
          <Step2SubscribePackage
            onSubscribe={handleStep2SubscribePackage}
            loading={loading}
            subscribed={packageSubscribed}
            hasStore={!!userStoreId}
            packageType={PACKAGE_TYPE}
            trialDays={TRIAL_DAYS}
          />
        )}

        {/* Step 3: Success */}
        {currentStep === 3 && (
          <Step3Success
            bonusAmount={hasBonusCoins ? BONUS_AMOUNT : 0}
            trialDays={hasPackageTrial ? TRIAL_DAYS : 0}
            trialEndDate={trialEndDate}
            onClose={onClose}
            hasBonusCoins={hasBonusCoins}
            hasPackageTrial={hasPackageTrial}
          />
        )}

        {/* Fallback: ถ้าไม่มี content ให้แสดงข้อความ */}
        {currentStep === 1 && !hasBonusCoins && !hasPackageTrial && (
          <div className="p-6 md:p-8 text-center">
            <div className="mb-6">
              <div className="w-24 h-24 mx-auto bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center shadow-lg">
                <span className="text-5xl">ℹ️</span>
              </div>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-3">
              ไม่มี Campaign Features
            </h2>
            <p className="text-gray-600 mb-6">
              Campaign นี้ยังไม่ได้ตั้งค่า features
            </p>
            <button
              onClick={onClose}
              className="w-full py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all"
            >
              ปิด
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Step 1: Claim Bonus
const Step1ClaimBonus = ({ bonusAmount, onClaim, loading, claimed, onAdvance, currentTheme }) => {
  return (
    <div className="p-6 md:p-8 text-center">
      {/* Progress Indicator */}
      <div className="mb-6">
        <div className="flex items-center justify-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center font-bold">
            1
          </div>
          <div className="h-1 w-16 bg-gray-200"></div>
          <div className="w-8 h-8 rounded-full bg-gray-200 text-gray-500 flex items-center justify-center font-bold">
            2
          </div>
          <div className="h-1 w-16 bg-gray-200"></div>
          <div className="w-8 h-8 rounded-full bg-gray-200 text-gray-500 flex items-center justify-center font-bold">
            3
          </div>
        </div>
        <p className="text-sm text-gray-500">ขั้นตอนที่ 1/3</p>
      </div>

      {/* Icon */}
      <div className="mb-6 animate-bounce">
        <div className="w-24 h-24 mx-auto bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center shadow-lg">
          <span className="text-5xl">🪙</span>
        </div>
      </div>

      {/* Title */}
      <h2 
        className="text-2xl md:text-3xl font-bold mb-3"
        style={{ color: currentTheme?.colors['text-primary'] || '#1E293B' }}
      >
        รับโบนัสฟรี!
      </h2>

      {/* Bonus Amount */}
      <div className="mb-6">
        <div className="inline-block bg-gradient-to-r from-yellow-400 to-yellow-600 text-white px-6 py-3 rounded-xl shadow-lg">
          <p className="text-sm opacity-90">คุณได้รับ</p>
          <p className="text-4xl font-bold">{bonusAmount.toLocaleString()}</p>
          <p className="text-sm opacity-90">Bonus Coins ฟรี!</p>
        </div>
      </div>

      {/* Description */}
      <p 
        className="mb-6"
        style={{ color: currentTheme?.colors['text-secondary'] || '#64748B' }}
      >
        ใช้ Bonus Coins สำหรับสมัคร Package และใช้งานในระบบ
        <br />
        <span 
          className="text-xs"
          style={{ color: currentTheme?.colors['text-secondary'] || '#64748B', opacity: 0.8 }}
        >
          (Bonus Coins แลกเป็นเงินสดไม่ได้)
        </span>
      </p>

      {/* CTA Button */}
      {!claimed ? (
        <button
          onClick={onClaim}
          disabled={loading}
          className="w-full py-4 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none text-lg"
          style={{
            background: `linear-gradient(to right, ${currentTheme?.colors.primary || '#10B981'}, ${currentTheme?.colors.accent || '#059669'})`
          }}
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              กำลังรับโบนัส...
            </span>
          ) : (
            '🎁 กดรับโบนัสเลย!'
          )}
        </button>
      ) : (
        <div className="space-y-3">
          <div className="w-full py-4 bg-green-500 text-white font-bold rounded-xl text-lg flex items-center justify-center gap-2">
            <span>✅</span>
            <span>รับโบนัสสำเร็จแล้ว!</span>
          </div>
          <p className="text-sm text-gray-600 text-center">
            คุณได้รับโบนัสนี้ไปแล้ว ไม่สามารถรับซ้ำได้
          </p>
          {onAdvance && (
            <button
              onClick={onAdvance}
              className="w-full py-3 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-xl transition-colors"
            >
              ต่อไป →
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// Step 2: Subscribe Package
const Step2SubscribePackage = ({ onSubscribe, loading, subscribed, hasStore, packageType = PACKAGE_TYPES.PREMIUM, trialDays = 14 }) => {
  const packageInfo = PACKAGES[packageType] || PACKAGES[PACKAGE_TYPES.PREMIUM];

  return (
    <div className="p-6 md:p-8 text-center">
      {/* Progress Indicator */}
      <div className="mb-6">
        <div className="flex items-center justify-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center font-bold">
            ✓
          </div>
          <div className="h-1 w-16 bg-green-500"></div>
          <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center font-bold">
            2
          </div>
          <div className="h-1 w-16 bg-gray-200"></div>
          <div className="w-8 h-8 rounded-full bg-gray-200 text-gray-500 flex items-center justify-center font-bold">
            3
          </div>
        </div>
        <p className="text-sm text-gray-500">ขั้นตอนที่ 2/3</p>
      </div>

      {/* Icon */}
      <div className="mb-6 animate-pulse">
        <div className="w-24 h-24 mx-auto bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center shadow-lg">
          <span className="text-5xl">🏆</span>
        </div>
      </div>

      {/* Title */}
      <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-3">
        สมัคร Premium Package
      </h2>

      {/* Package Info */}
      <div className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border-2 border-green-200">
        <p className="text-lg font-bold text-gray-800 mb-2">{packageInfo.name}</p>
        <p className="text-sm text-gray-600 mb-3">{packageInfo.description}</p>
        
        {/* Features */}
        <div className="text-left space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-green-500">✓</span>
            <span>รูปสินค้า: {packageInfo.features.maxProductImages} รูป/ชิ้น</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-green-500">✓</span>
            <span>สินค้าพร้อมขาย: {packageInfo.features.maxActiveProducts} ชิ้น</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-green-500">✓</span>
            <span>QR Code PromptPay: ✅</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-green-500">✓</span>
            <span>เก็บปลายทาง (COD): ✅</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-green-500">✓</span>
            <span>ระบบวิเคราะห์ข้อมูล: ✅</span>
          </div>
        </div>

        {/* Price */}
        <div className="mt-4 pt-4 border-t border-green-200">
          <p className="text-xs text-gray-500 mb-1">ใช้ Bonus Coins</p>
          <p className="text-2xl font-bold text-green-600">ฟรี 14 วัน!</p>
        </div>
      </div>

      {/* Description */}
      <p className="text-gray-600 mb-6 text-sm">
        {hasStore 
          ? 'สมัคร Premium Package เพื่อใช้ฟีเจอร์ครบวงจร'
          : 'เราจะสร้างร้านค้าให้คุณอัตโนมัติ พร้อมสมัคร Premium Package'}
      </p>

      {/* CTA Button */}
      {!subscribed ? (
        <button
          onClick={onSubscribe}
          disabled={loading}
          className="w-full py-4 bg-gradient-to-r from-green-500 to-green-600 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed text-lg"
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              กำลังสมัคร...
            </span>
          ) : (
            '🚀 สมัครเลย!'
          )}
        </button>
      ) : (
        <div className="w-full py-4 bg-green-500 text-white font-bold rounded-xl text-lg flex items-center justify-center gap-2">
          <span>✅</span>
          <span>สมัครสำเร็จ!</span>
        </div>
      )}
    </div>
  );
};

// Step 3: Success
const Step3Success = ({ bonusAmount, trialDays, trialEndDate, onClose, hasBonusCoins = true, hasPackageTrial = true }) => {
  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="p-6 md:p-8 text-center">
      {/* Success Animation */}
      <div className="mb-6 animate-bounce">
        <div className="w-32 h-32 mx-auto bg-gradient-to-br from-yellow-400 via-green-500 to-emerald-600 rounded-full flex items-center justify-center shadow-2xl relative">
          <span className="text-7xl">🎉</span>
          {/* Sparkles */}
          <div className="absolute -top-2 -right-2 text-3xl animate-ping" style={{ animationDelay: '0s' }}>✨</div>
          <div className="absolute -bottom-2 -left-2 text-3xl animate-ping" style={{ animationDelay: '0.5s' }}>⭐</div>
          <div className="absolute top-1/2 -left-4 text-2xl animate-pulse" style={{ animationDelay: '1s' }}>💫</div>
        </div>
      </div>

      {/* Title */}
      <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
        ยินดีด้วย! 🎊
      </h2>

      {/* Subtitle */}
      <p className="text-lg text-gray-600 mb-6">
        คุณได้รับสิทธิพิเศษทั้งหมดแล้ว!
      </p>

      {/* Rewards List */}
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 mb-6 border-2 border-green-200">
        <h3 className="text-xl font-bold text-gray-800 mb-4">สิ่งที่คุณได้รับ:</h3>
        
        <div className="space-y-3 text-left">
          {/* Bonus Coins - แสดงเฉพาะถ้ามี */}
          {hasBonusCoins && bonusAmount > 0 && (
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm">
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">🪙</span>
              </div>
              <div className="flex-1">
                <p className="font-bold text-gray-800">Bonus Coins</p>
                <p className="text-sm text-gray-600">{bonusAmount.toLocaleString()} เหรียญ</p>
              </div>
            </div>
          )}

          {/* Package Trial - แสดงเฉพาะถ้ามี */}
          {hasPackageTrial && trialDays > 0 && (
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">🏆</span>
              </div>
              <div className="flex-1">
                <p className="font-bold text-gray-800">Premium Package</p>
                <p className="text-sm text-gray-600">ทดลองใช้ฟรี {trialDays} วัน</p>
              </div>
            </div>
          )}

          {/* Features - แสดงเฉพาะถ้ามี Package Trial */}
          {hasPackageTrial && (
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">✨</span>
              </div>
              <div className="flex-1">
                <p className="font-bold text-gray-800">ฟีเจอร์ครบวงจร</p>
                <p className="text-sm text-gray-600">รูปสินค้าไม่จำกัด • QR Code • COD • Analytics</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Trial End Date */}
      {trialEndDate && (
        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4 mb-6">
          <p className="text-sm text-yellow-800 font-medium mb-1">⏰ หมดอายุ:</p>
          <p className="text-lg font-bold text-yellow-900">{formatDate(trialEndDate)}</p>
          <p className="text-xs text-yellow-700 mt-1">
            หลังจากวันนี้ Package จะกลับไปเป็น Free (สามารถสมัครต่อได้)
          </p>
        </div>
      )}

      {/* CTA Button */}
      <button
        onClick={onClose}
        className="w-full py-4 bg-gradient-to-r from-green-500 to-green-600 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:scale-105 text-lg"
      >
        เริ่มใช้งานเลย! 🚀
      </button>
    </div>
  );
};

export default FreeTrialPromotionModal;

